export interface DecimalFieldPolicy {
    allowExponent: boolean;
    /** Applied only when the text is committed, by rounding. */
    maxDecimals?: number;
}

export type DecimalSurface = 'dynamic' | 'slider' | 'geogridScalar' | 'geogridItem' | 'esalGrid' | 'esalSummary' | 'coordinates' | 'admin';

export type DecimalParseResult =
    | { kind: 'value'; value: number; normalized: string }
    | { kind: 'draft'; normalized: string; committable: number | null }
    | { kind: 'ambiguous' }
    | { kind: 'invalid'; reason: 'separators' | 'characters' | 'nonFinite' | 'exponent' };

export type DecimalErrorKind = 'ambiguous' | 'notANumber';

export const DECIMAL_TEXT_INPUT_ATTRIBUTES = { type: 'text', inputMode: 'decimal' } as const;

const DEFAULT_DYNAMIC_DECIMALS = 2;
const SLIDER_DECIMALS = 2;

const SLIDER_POLICY: DecimalFieldPolicy = { allowExponent: false, maxDecimals: SLIDER_DECIMALS };
const UNLIMITED_POLICY: DecimalFieldPolicy = { allowExponent: true };

export const resolveDecimalFieldPolicy = (field: { renderAs?: string; decimalPlaces?: number }, surface: DecimalSurface): DecimalFieldPolicy => {
    if (field.renderAs === 'slider') return SLIDER_POLICY;

    switch (surface) {
        case 'dynamic':
            return { allowExponent: true, maxDecimals: field.decimalPlaces ?? DEFAULT_DYNAMIC_DECIMALS };
        case 'slider':
        case 'geogridScalar':
            return SLIDER_POLICY;
        case 'esalSummary':
            return { allowExponent: false, maxDecimals: Math.max(field.decimalPlaces ?? 0, SLIDER_DECIMALS) };
        case 'geogridItem':
        case 'esalGrid':
        case 'coordinates':
        case 'admin':
            return UNLIMITED_POLICY;
    }
};

// A comma followed by exactly three digits reads as a thousands group in many locales:
// refuse to guess instead of silently turning 1,000 into 1.
const AMBIGUOUS_THOUSANDS_GROUP = /^[-+]?(\d{1,3}),\d{3}$/;
const ALLOWED_CHARACTERS = /^[-+0-9.,eE]*$/;
// A leading sign, + included: the browser number input accepted +1.5 as 1.5
const MANTISSA = /^[-+]?[0-9.,]*$/;
const EXPONENT_DIGITS = /^[+-]?\d*$/;

const roundToDecimals = (value: number, decimals: number): number => Number(value.toFixed(decimals));

const countFractionDigits = (mantissa: string): number => {
    const separatorIndex = mantissa.search(/[.,]/);
    return separatorIndex === -1 ? 0 : mantissa.length - separatorIndex - 1;
};

export const parseDecimalInput = (rawText: string, policy: DecimalFieldPolicy): DecimalParseResult => {
    const text = rawText.trim();
    if (!ALLOWED_CHARACTERS.test(text)) return { kind: 'invalid', reason: 'characters' };

    const exponentIndex = text.search(/[eE]/);
    const hasExponent = exponentIndex !== -1;
    if (hasExponent && !policy.allowExponent) return { kind: 'invalid', reason: 'exponent' };

    const mantissa = hasExponent ? text.slice(0, exponentIndex) : text;
    const exponent = hasExponent ? text.slice(exponentIndex + 1) : '';
    if (!MANTISSA.test(mantissa) || !EXPONENT_DIGITS.test(exponent)) return { kind: 'invalid', reason: 'characters' };

    const dotCount = (mantissa.match(/\./g) ?? []).length;
    const commaCount = (mantissa.match(/,/g) ?? []).length;
    if (dotCount + commaCount > 1) return { kind: 'invalid', reason: 'separators' };
    if (commaCount === 1 && hasExponent) return { kind: 'invalid', reason: 'separators' };

    const normalizedMantissa = mantissa.replace(',', '.');
    const normalized = hasExponent ? `${normalizedMantissa}e${exponent}` : normalizedMantissa;
    const mantissaHasDigits = /\d/.test(mantissa);

    if (!mantissaHasDigits) {
        return hasExponent ? { kind: 'invalid', reason: 'characters' } : { kind: 'draft', normalized, committable: null };
    }
    if (hasExponent && !/\d/.test(exponent)) return { kind: 'draft', normalized, committable: null };

    const endsWithSeparator = !hasExponent && /[.,]$/.test(mantissa);
    if (!endsWithSeparator && commaCount === 1 && AMBIGUOUS_THOUSANDS_GROUP.test(mantissa) && Number(mantissa.split(',')[0]) !== 0) {
        return { kind: 'ambiguous' };
    }

    const value = Number(normalized);
    if (!Number.isFinite(value)) return { kind: 'invalid', reason: 'nonFinite' };
    if (endsWithSeparator) return { kind: 'draft', normalized, committable: value };

    const { maxDecimals } = policy;
    if (maxDecimals !== undefined && (countFractionDigits(mantissa) > maxDecimals || roundToDecimals(value, maxDecimals) !== value)) {
        return { kind: 'draft', normalized, committable: roundToDecimals(value, maxDecimals) };
    }
    return { kind: 'value', value, normalized };
};

export const isCommittable = (raw: unknown, policy: DecimalFieldPolicy): boolean => {
    if (typeof raw === 'number') return Number.isFinite(raw);
    if (typeof raw === 'string') return parseDecimalInput(raw, policy).kind === 'value';
    return false;
};

export const isPendingRawText = (raw: unknown, policy: DecimalFieldPolicy): boolean => typeof raw === 'string' && raw.trim() !== '' && !isCommittable(raw, policy);

/** Chooses only the wording of an error; whether a value is usable is decided by isCommittable. */
export const decimalErrorMessageKind = (raw: unknown): DecimalErrorKind | null => {
    if (typeof raw !== 'string' || raw.trim() === '') return null;
    const parsed = parseDecimalInput(raw, UNLIMITED_POLICY);
    if (parsed.kind === 'ambiguous') return 'ambiguous';
    return parsed.kind === 'value' ? null : 'notANumber';
};
